<!-- Skeleton Loading State -->
<div class="coaching-container" *ngIf="isProcessing && !data">
  <!-- Eyebrow -->
  <div class="co-eyebrow">
    <ion-icon name="ribbon-outline" aria-hidden="true"></ion-icon>
    <span>{{ 'NOVA_PAGE_COACHING' | translate }}</span>
  </div>

  <div class="coaching-scroll">
    <div class="score-section" aria-busy="true" [attr.aria-label]="'NOVA_LOADING_PERFORMANCE_SCORES' | translate">
      <div class="score-ring-wrapper">
        <div class="skeleton-score-ring" aria-hidden="true"></div>
        <ion-skeleton-text [animated]="true" class="skeleton-label"></ion-skeleton-text>
      </div>
      <div class="score-ring-wrapper">
        <div class="skeleton-score-ring small" aria-hidden="true"></div>
        <ion-skeleton-text [animated]="true" class="skeleton-label"></ion-skeleton-text>
      </div>
    </div>

    <div class="section">
      <h3 class="section-title">{{ 'NOVA_SENTIMENT_BREAKDOWN' | translate }}</h3>
      <div class="sentiment-bars">
        <div class="sentiment-row" *ngFor="let i of [1,2,3]" aria-hidden="true">
          <ion-skeleton-text [animated]="true" class="skeleton-sentiment-label"></ion-skeleton-text>
          <div class="bar-track">
            <ion-skeleton-text [animated]="true" class="skeleton-bar-fill"></ion-skeleton-text>
          </div>
          <ion-skeleton-text [animated]="true" class="skeleton-sentiment-pct"></ion-skeleton-text>
        </div>
      </div>
    </div>

    <div class="section">
      <h3 class="section-title">
        <ion-icon name="checkmark-circle" class="success-icon" aria-hidden="true"></ion-icon> {{ 'NOVA_WHAT_WENT_WELL' | translate }}
      </h3>
      <div *ngFor="let i of [1,2,3]" class="skeleton-list-row">
        <ion-skeleton-text [animated]="true" class="skeleton-list-line"></ion-skeleton-text>
      </div>
    </div>

    <div class="section">
      <h3 class="section-title">
        <ion-icon name="trending-up" class="improve-icon" aria-hidden="true"></ion-icon> {{ 'NOVA_AREAS_FOR_IMPROVEMENT' | translate }}
      </h3>
      <div *ngFor="let i of [1,2]" class="skeleton-list-row">
        <ion-skeleton-text [animated]="true" class="skeleton-list-line w-90"></ion-skeleton-text>
      </div>
    </div>
  </div>
</div>

<!-- Actual Content -->
<div class="coaching-container" *ngIf="data">
  <!-- Eyebrow -->
  <div class="co-eyebrow">
    <ion-icon name="ribbon-outline" aria-hidden="true"></ion-icon>
    <span>{{ 'NOVA_PAGE_COACHING' | translate }}</span>
  </div>

  <div class="coaching-scroll">
    <!-- Hero: Score Rings -->
    <div class="score-section">
      <div class="score-ring-wrapper">
        <div
          class="score-ring"
          [style.background]="getScoreRingStyle()"
          [attr.aria-label]="'NOVA_PERFORMANCE_SCORE_LABEL' | translate: { score: performancePercent }"
          role="img">
          <div class="score-inner">
            <span class="score-value">{{ performancePercent }}</span>
            <span class="score-unit">/ 100</span>
          </div>
        </div>
        <span class="score-label">{{ 'NOVA_PERFORMANCE' | translate }}</span>
      </div>
      <div class="score-ring-wrapper">
        <div
          class="score-ring small"
          [style.background]="getConfidenceRingStyle()"
          [attr.aria-label]="'NOVA_CONFIDENCE_SCORE_LABEL' | translate: { percent: confidencePercent }"
          role="img">
          <div class="score-inner">
            <span class="score-value">{{ confidencePercent }}%</span>
          </div>
        </div>
        <span class="score-label">{{ 'NOVA_CONFIDENCE' | translate }}</span>
      </div>
    </div>

    <!-- Sentiment Breakdown -->
    <div class="section" *ngIf="data.sentiment_analysis">
      <h3 class="section-title">
        <ion-icon name="bar-chart-outline" aria-hidden="true"></ion-icon> {{ 'NOVA_SENTIMENT_BREAKDOWN' | translate }}
      </h3>
      <div class="sentiment-bars">
        <div class="sentiment-row">
          <span class="sentiment-type positive">{{ 'NOVA_SENTIMENT_POSITIVE' | translate }}</span>
          <div
            class="bar-track"
            [attr.aria-label]="'NOVA_VALUE_PERCENT_LABEL' | translate: { name: ('NOVA_SENTIMENT_POSITIVE' | translate), percent: getSentimentPct(data.sentiment_analysis.positive_percent) }"
            role="meter"
            [attr.aria-valuenow]="getSentimentPct(data.sentiment_analysis.positive_percent)"
            aria-valuemin="0"
            aria-valuemax="100">
            <div class="bar-fill positive" [style.width.%]="getSentimentPct(data.sentiment_analysis.positive_percent)"></div>
          </div>
          <span class="sentiment-value">{{ getSentimentPct(data.sentiment_analysis.positive_percent) | number:'1.1-1' }}%</span>
        </div>
        <div class="sentiment-row">
          <span class="sentiment-type neutral">{{ 'NOVA_SENTIMENT_NEUTRAL' | translate }}</span>
          <div
            class="bar-track"
            [attr.aria-label]="'NOVA_VALUE_PERCENT_LABEL' | translate: { name: ('NOVA_SENTIMENT_NEUTRAL' | translate), percent: getSentimentPct(data.sentiment_analysis.neutral_percent) }"
            role="meter"
            [attr.aria-valuenow]="getSentimentPct(data.sentiment_analysis.neutral_percent)"
            aria-valuemin="0"
            aria-valuemax="100">
            <div class="bar-fill neutral" [style.width.%]="getSentimentPct(data.sentiment_analysis.neutral_percent)"></div>
          </div>
          <span class="sentiment-value">{{ getSentimentPct(data.sentiment_analysis.neutral_percent) | number:'1.1-1' }}%</span>
        </div>
        <div class="sentiment-row">
          <span class="sentiment-type negative">{{ 'NOVA_SENTIMENT_NEGATIVE' | translate }}</span>
          <div
            class="bar-track"
            [attr.aria-label]="'NOVA_VALUE_PERCENT_LABEL' | translate: { name: ('NOVA_SENTIMENT_NEGATIVE' | translate), percent: getSentimentPct(data.sentiment_analysis.negative_percent) }"
            role="meter"
            [attr.aria-valuenow]="getSentimentPct(data.sentiment_analysis.negative_percent)"
            aria-valuemin="0"
            aria-valuemax="100">
            <div class="bar-fill negative" [style.width.%]="getSentimentPct(data.sentiment_analysis.negative_percent)"></div>
          </div>
          <span class="sentiment-value">{{ getSentimentPct(data.sentiment_analysis.negative_percent) | number:'1.1-1' }}%</span>
        </div>
      </div>
    </div>

    <!-- What Went Well -->
    <div class="section" *ngIf="data.coaching_tips?.what_went_well?.length">
      <h3 class="section-title">
        <ion-icon name="checkmark-circle" class="success-icon" aria-hidden="true"></ion-icon> {{ 'NOVA_WHAT_WENT_WELL' | translate }}
      </h3>
      <ul class="coaching-list success">
        <li *ngFor="let item of data.coaching_tips.what_went_well">{{ item }}</li>
      </ul>
    </div>

    <!-- Areas for Improvement -->
    <div class="section" *ngIf="data.coaching_tips?.areas_for_improvement?.length">
      <h3 class="section-title">
        <ion-icon name="trending-up" class="improve-icon" aria-hidden="true"></ion-icon> {{ 'NOVA_AREAS_FOR_IMPROVEMENT' | translate }}
      </h3>
      <ul class="coaching-list improve">
        <li *ngFor="let item of data.coaching_tips.areas_for_improvement">{{ item }}</li>
      </ul>
    </div>

    <!-- Specific Suggestions -->
    <div class="section" *ngIf="data.coaching_tips?.specific_suggestions?.length">
      <h3 class="section-title">
        <ion-icon name="bulb-outline" class="suggestion-icon" aria-hidden="true"></ion-icon> {{ 'SUGGESTIONS' | translate }}
      </h3>
      <ul class="coaching-list suggestion">
        <li *ngFor="let item of data.coaching_tips.specific_suggestions">{{ item }}</li>
      </ul>
    </div>

    <!-- Performance Overview -->
    <div class="section" *ngIf="data.performance_overview">
      <h3 class="section-title">
        <ion-icon name="analytics-outline" aria-hidden="true"></ion-icon> {{ 'NOVA_PAGE_OVERVIEW' | translate }}
      </h3>
      <p class="overview-text">{{ data.performance_overview }}</p>
    </div>
  </div>
</div>
